import styled, { css } from "styled-components";

import {
    SchoolOutlined as SchoolOutlinedIcon,
    PersonOutline as PersonOutlineIcon,
    ThumbUpAltOutlined as ThumbUpAltOutlinedIcon,
    ThumbDownAltOutlined as ThumbDownAltOutlinedIcon,
    StarOutline as StarOutlineIcon,
    Star as StarIcon,
} from '@material-ui/icons';

import ProImage from "components/Icons/ProImage";
import EliteImage from "components/Icons/EliteImage";
import { Button } from "@mui/material";

interface ItemContainerProps {
  $isNew: boolean;
  disableCardStyle?: boolean;
}

export const ItemContainer = styled.div<{ $isNew?: boolean; disableCardStyle?: boolean }>`
  display: flex;
  flex-direction: row; /* Default to row for desktop */
  align-items: center; /* Center items vertically */
  padding: 1rem 1rem 1rem 1.5rem; /* More padding on the left */
  margin-bottom: 1rem; 
  border-bottom: ${({ $isNew }) => $isNew ? '2px solid #13CA82' : 'none'}; 
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  ${({ disableCardStyle }) => !disableCardStyle && css`
  background-color: #ffffff; 
  border: 1px solid #0B4598; 
  border-radius: 8px; 
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); 
  `}

  @media (max-width: 465px) {
    flex-direction: column; /* Stack TopSection and ActionsWrapper */
    align-items: stretch; /* Make children take full width if they desire */
    padding: 0.75rem 0.75rem 0.75rem 1rem; /* More padding on the left for mobile too */
    gap: 0.75rem; /* Add gap between stacked elements */
  }

  &:last-child {
    margin-bottom: 0; 
  }
`;

export const TopSection = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start; /* Better alignment for varied content heights */
  flex-grow: 1; /* Allow TopSection to take space if ItemContainer is flex row */

  @media (max-width: 465px) {
    flex-direction: row; /* Keep image and text side-by-side on mobile */
    align-items: flex-start;
    text-align: left; /* Left align text */
  }
`;

export const Image = styled.img`
  object-fit: cover;
  width: 6rem;
  height: 6rem;
  border-radius: 100%;
  margin-right: 1rem; /* Default margin for desktop */
  flex-shrink: 0; /* Prevent image from shrinking */
  
  @media (max-width: 465px) {
    width: 3.5rem; /* Smaller for mobile */
    height: 3.5rem;
    margin-right: 0.75rem; /* Less margin on mobile */
  }
`

export const AvatarWrapper = styled.div`
  width: 6rem;
  height: 6rem;
  margin-right: 1rem; /* Default margin for desktop */
  flex-shrink: 0; /* Prevent avatar from shrinking */
  
  @media (max-width: 465px) {
    width: 3.5rem; /* Smaller for mobile */
    height: 3.5rem;
    margin-right: 0.75rem; /* Less margin on mobile */
  }
  
  /* Override AvatarWithInitials default size */
  & > div {
    width: 100%;
    height: 100%;
  }
`

export const Titles = styled.div<{$isDetailView?: boolean}>`
  display: flex;
  flex-direction: column;
  justify-content: center; /* Vertically center content in Titles */
  flex-grow: 1; 
  padding-right: ${({$isDetailView}) => $isDetailView ? '0' : '0.5rem'}; 

  @media (max-width: 465px) {
    width: 100%; 
    padding-right: 0; /* No padding on mobile */
  }
`;

export const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  @media (max-width: 465px) {
    width: 100%;
    justify-content: space-between;
    padding: 0 1rem; /* Add horizontal padding on mobile to match parent container gutters */
  }
`;

export const Badge = styled.div`
  width: 24px;
  margin-bottom: 0.25rem;
`;

export const CandidateName = styled.div<{$isDetailView: boolean, $isHidden: boolean}>`
  font-size: ${({$isDetailView}) => $isDetailView ? '1.5rem' : '1rem'};
  padding-right: ${({$isDetailView}) => $isDetailView ? '6rem' : '0'};
  color: ${({$isHidden}) => $isHidden ? 'var(--error-main, #FB7672)' : 'rgba(0, 0, 0, 0.87)'};
  font-weight: 600;
  margin-bottom: 0.25rem;

  @media (max-width: 465px) {
    padding-right: 0;
    font-size: 0.9rem; /* Slightly smaller on mobile */
  }
  @media (max-width: 768px) {
    font-size: ${({$isDetailView}) => $isDetailView ? '1.25rem' : '1rem'};
  }
`;

export const CandidateExperience = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  flex-wrap: wrap; /* Allow wrapping on very small screens */
  
  @media (max-width: 465px) {
    font-size: 0.7rem;
  }
`

export const StyledProImage = styled(ProImage)`
  height: 24px !important;;
`

export const StyledEliteImage = styled(EliteImage)`
  height: 24px !important;;
`

export const StyledSchoolOutlined = styled(SchoolOutlinedIcon)`
  color: #0B4598;
  width: 18px;
  margin-right: 0.25rem;
`

export const StyledPersonOutlineIcon = styled(PersonOutlineIcon)`
  color: #fff;
  width: 18px;
  margin-right: 0.25rem;
`

const commonBadgeClasses = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.75rem;
  color: #fff;
  font-weight: 600;
  font-size: 0.75rem;
  border-radius: 4px;
  white-space: nowrap;

  @media (max-width: 465px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }
`

export const NewCandidateBadge = styled.div<{$isDetailView: boolean}>`
  ${commonBadgeClasses};
  background-color: #13CA82;
`

export const ShortlistedCandidateBadge = styled.div<{$isDetailView: boolean}>`
  ${({$isDetailView}) => $isDetailView ? 'position: absolute; top: 0; right: 0;' : ''}
  ${commonBadgeClasses};
  background-color: #988C81FF;
`

export const DeclinedCandidateBadge = styled.div<{$isDetailView: boolean}>`
  ${({$isDetailView}) => $isDetailView ? 'position: absolute; top: 0; right: 0;' : ''}
  ${commonBadgeClasses};
  background-color: var(--error-main, #FB7672);
`

export const StyledThumbUpAltOutlinedIcon = styled(ThumbUpAltOutlinedIcon)`
  color: #fff;
  width: 18px;
  margin-right: 0.25rem;
`
export const StyledThumbDownAltOutlinedIcon = styled(ThumbDownAltOutlinedIcon)`
  color: #fff;
  width: 18px;
  margin-right: 0.25rem;
`

export const NoApplicationsMessage = styled.div`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
`

export const DetailViewInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;

  @media (max-width: 768px) {
  gap: 1rem;
  }
`;

export const DetailViewInfoItem = styled.div<{$isGraduationYear?: boolean}>`
    display: flex;
    ${({$isGraduationYear}) => $isGraduationYear ? 'flex-direction: column;' : ''}
    align-items: center;
    gap: 0.25rem;
    height: 44px;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);

    @media (max-width: 768px) {
      text-align: center;
    }
`;

export const GraduationYear = styled.div`
    font-size: 1rem;
    color: rgba(0, 0, 0, 0.87);
    font-weight: 600;
`;

export const GraduationYearLabel = styled.div`
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
`;

export const ViewCandidateButton = styled(Button)`
  white-space: nowrap !important;
  min-width: auto !important;
  padding: 0.5rem 1rem !important;

  @media (max-width: 465px) {
    font-size: 0.75rem !important;
    padding: 0.4rem 0.8rem !important;
  }
`;

export const StarIconActive = styled(StarIcon)``;

export const StarIconInactive = styled(StarOutlineIcon)``;

export const NoRatingText = styled.div`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
`;

export const NoRatingTextLarge = styled.div`
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
`;

export const RatingCountText = styled.span`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
`;

export const VerticalInfoStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const InfoLine = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
`;